/**
* @module lib
* @author Charles Blais <charles.blais@canada.ca>
*/

/**
 * Take a Vs30 value array and convert to a string friendly format
 * @param {Array} vs30 
 */
export const vs30toSiteDesignation = (vs30: Vs30) => {
  if(vs30.length === 2){
    if(vs30[0] >= 1500) return 'XA';
    if(vs30[0] >= 760) return 'XB';
    if(vs30[0] >= 360) return 'XC';
    if(vs30[0] >= 180) return 'XD';
    return 'XE';
  }
  return `X${vs30[0]}`;
}

/**
 * Flatten the site designations object where the key the
 * site designation to a clean array.
 * 
 * A key is added to each site designation which the site desgination
 * print friendly name.
 */
export const flattenSiteDesignations = (
  siteDesignations: SiteDesignationsXVResponse | SiteDesignationsXSResponse
): SiteDesignation[] => {
  // We use a reducer to convert as object to remove duplicate keys
  // this could happen if the userSpecified matches a request
  const data = Object.keys(siteDesignations).reduce((acc, key) => {
    if( key.startsWith('_') || key === 'metadata') return acc;

    // create a converted key for the data
    // @ts-ignore: string key used to index predefined structure
    let cKey = ( key === 'userSpecified') ? vs30toSiteDesignation(siteDesignations[key][0].vs30) : key;

    // @ts-ignore: string key used to index predefined structure
    acc[cKey] = siteDesignations[key].map<SiteDesignation>((
      site: SiteDesignationXSResponse | SiteDesignationXVResponse
    ) => {
      return {
        siteDesignation: cKey,
        ...site,
      }
    });
    return acc;
  }, {});

  const flatten: SiteDesignation[] = Object.values<SiteDesignation[]>(data).flat()

  // Note we define "any" since sort seems to only accept "unknown"
  const orderedPoe = flatten.sort((
    a: SiteDesignationXS | SiteDesignationXV,
    b: SiteDesignationXS | SiteDesignationXV
  ) => {
    return (Number(a.poe50) > Number(b.poe50)) ? -1 : 1
  })

  return orderedPoe.sort((
    a: SiteDesignationXS | SiteDesignationXV,
    b: SiteDesignationXS | SiteDesignationXV
  ) => {
    if(a.vs30.length !== b.vs30.length)
      return (a.vs30.length === 1) ? -1 : 1
    return (a.vs30[0] > b.vs30[0]) ? 1 : -1
  });
}


/**
 * Calculate interpolated SA between sa 1 and sa 2 and inter_per.
 * 
 * "_per" = percentage (ex: 2.0)
 * 
 * See Michal Kolaj for more information
 * 
 * @param {float} sa_per1 
 * @param {float} sa_val1 
 * @param {float} sa_per2 
 * @param {float} sa_val2 
 * @param {float} inter_per 
 */
export const calcInterpolatedSA = (
  sa_per1: number,
  sa_val1: number,
  sa_per2: number,
  sa_val2: number,
  inter_per: number
): number => {
  // Convert values to log for interpolation
  const sa_val1_log = Math.log(sa_val1);
  const sa_val2_log = Math.log(sa_val2);
  const sa_per1_log = Math.log(sa_per1);
  const sa_per2_log = Math.log(sa_per2);
  const inter_per_log = Math.log(inter_per);
  const sa_per_log = sa_val1_log + (inter_per_log - sa_per1_log) * ((sa_val2_log - sa_val1_log)/(sa_per2_log - sa_per1_log))
  return Math.exp(sa_per_log);
}